import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, CircularProgress, Alert,
  MenuItem, TextField, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow
} from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import ModuleHeader from './components/ModuleHeader';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { API_URL } from './config';

interface Ciclo  { id: number; nome: string; ativo: boolean; }
interface Area   { id: number; nome: string; zona: string; }
interface Agente { id: number; nome: string; matricula: string; }

interface Linha {
  data: string;
  quarteirao_concluido: number;
  imoveis_informados: number;
  imoveis_residencia: number; imoveis_comercio: number; imoveis_terreno_baldio: number;
  imoveis_ponto_estrategico: number; imoveis_outros: number; total_imoveis: number;
  trat_focal: number; trat_perifocal: number; inspecionados: number;
  recuperados: number; recusados: number; fechados: number;
  amostras_coletadas: number;
  dep_a1: number; dep_a2: number; dep_b: number; dep_c: number;
  dep_d1: number; dep_d2: number; dep_e: number; dep_total: number;
  dep_eliminados: number; dep_tratados: number;
  larvicida_total_gramas: number;
  hdt: number; media: number;
}

interface Totais {
  quarteirao_concluido: number;
  imoveis_informados: number;
  imoveis_residencia: number; imoveis_comercio: number; imoveis_terreno_baldio: number;
  imoveis_ponto_estrategico: number; imoveis_outros: number; total_imoveis: number;
  trat_focal: number; trat_perifocal: number; inspecionados: number;
  recuperados: number; recusados: number; fechados: number;
  amostras_coletadas: number;
  dep_a1: number; dep_a2: number; dep_b: number; dep_c: number;
  dep_d1: number; dep_d2: number; dep_e: number; dep_total: number;
  dep_eliminados: number; dep_tratados: number;
  larvicida_total_gramas: number;
  hdt: number; media: number;
}

interface ApiData { linhas: Linha[]; totais: Totais; ses_disponiveis: number[]; }

interface ColDef {
  key: keyof Linha; label: string; fmt: (v: any) => any;
  headerSx?: object; cellSx?: object;
}

// ──── Paleta Designer (tema escuro) ────
const C = {
  bg:         '#030b16',
  bgPanel:    '#071323',
  bgRow:      '#0a1727',
  bgRowAlt:   '#0c1c30',
  cyan:       '#00b8cc',
  cyanBorder: 'rgba(0,184,204,0.22)',
  orange:     '#e85d26',
  white:      '#f0f4f8',
  grayLt:     '#b0bcc8',
  border:     'rgba(255,255,255,0.07)',
  borderMid:  'rgba(255,255,255,0.18)',
  green:      '#22c55e',
  purple:     '#8b5cf6',
  amber:      '#f59e0b',
};

// ──── Estilo dark dos filtros (TextField select) ────
const FIELD_SX = {
  '& .MuiInputBase-root': {
    backgroundColor: '#0a1830',
    color: C.white,
    fontFamily: "'Inter', sans-serif",
  },
  '& .MuiInputBase-input': {
    color: C.white,
  },
  '& .MuiInputLabel-root': {
    color: C.grayLt,
    fontFamily: "'Inter', sans-serif",
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: C.cyan,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: C.cyanBorder,
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: C.cyan,
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: C.cyan,
    borderWidth: '1px',
  },
  '& .MuiSelect-icon': {
    color: C.grayLt,
  },
};

const DATA_HEADER_SX = {
  bgcolor: '#0a1830',
  color: C.cyan,
  fontWeight: 700,
  fontSize: 10.5,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  fontFamily: "'Inter', sans-serif",
  borderBottom: `1px solid ${C.cyanBorder}`,
};
const DATA_CELL_SX = {
  bgcolor: '#0a1727',
  color: C.white,
  fontWeight: 600,
  fontFamily: "'Inter', sans-serif",
  borderRight: `2px solid ${C.cyanBorder}`,
};

const COL_GROUPS: { label: string; color: string; bodyColor: string; cols: ColDef[] }[] = [
  { label: 'DATA', color: C.grayLt, bodyColor: 'transparent', cols: [
      { key: 'data',                 label: 'Data',    fmt: (v: any) => new Date(v + 'T00:00:00').toLocaleDateString('pt-BR'), headerSx: DATA_HEADER_SX, cellSx: DATA_CELL_SX },
      { key: 'quarteirao_concluido', label: 'Qrt.C',  fmt: (v: any) => v > 0 ? v : '—', headerSx: DATA_HEADER_SX, cellSx: DATA_CELL_SX },
      { key: 'imoveis_informados',   label: 'Im.Inf.', fmt: (v: any) => v },
  ]},
  { label: 'IMÓVEIS TRABALHADOS', color: C.cyan, bodyColor: 'transparent', cols: [
      { key: 'imoveis_residencia',        label: 'Resid.',    fmt: (v: any) => v },
      { key: 'imoveis_comercio',          label: 'Com.',      fmt: (v: any) => v },
      { key: 'imoveis_terreno_baldio',    label: 'T.Baldio',  fmt: (v: any) => v },
      { key: 'imoveis_ponto_estrategico', label: 'P.Estrat.', fmt: (v: any) => v },
      { key: 'imoveis_outros',            label: 'Outro',     fmt: (v: any) => v },
      { key: 'total_imoveis',             label: 'TOTAL',     fmt: (v: any) => v },
  ]},
  { label: 'SITUAÇÃO DOS IMÓVEIS', color: C.amber, bodyColor: 'transparent', cols: [
      { key: 'trat_focal',     label: 'Tr.Focal',  fmt: (v: any) => v },
      { key: 'trat_perifocal', label: 'Tr.Perif.', fmt: (v: any) => v },
      { key: 'inspecionados',      label: 'Inspec.',   fmt: (v: any) => v },
      { key: 'amostras_coletadas', label: 'Amostras', fmt: (v: any) => v },
      { key: 'recusados',      label: 'Recusa',    fmt: (v: any) => v },
      { key: 'fechados',       label: 'Fechados',  fmt: (v: any) => v },
      { key: 'recuperados',        label: 'Recuper.',  fmt: (v: any) => v },
  ]},
  { label: 'DEPOSITOS INSPECIONADOS', color: C.green, bodyColor: 'transparent', cols: [
      { key: 'dep_a1',         label: 'A1',      fmt: (v: any) => v },
      { key: 'dep_a2',         label: 'A2',      fmt: (v: any) => v },
      { key: 'dep_b',          label: 'B',       fmt: (v: any) => v },
      { key: 'dep_c',          label: 'C',       fmt: (v: any) => v },
      { key: 'dep_d1',         label: 'D1',      fmt: (v: any) => v },
      { key: 'dep_d2',         label: 'D2',      fmt: (v: any) => v },
      { key: 'dep_e',          label: 'E',       fmt: (v: any) => v },
      { key: 'dep_total',      label: 'Total',   fmt: (v: any) => v },
      { key: 'dep_eliminados', label: 'Elimin.', fmt: (v: any) => v },
  ]},
  { label: 'LARVICIDA L1+L2', color: C.orange, bodyColor: 'transparent', cols: [
      { key: 'larvicida_total_gramas', label: 'Gramas', fmt: (v: any) => v > 0 ? parseFloat(v).toFixed(1).replace('.', ',') + ' g' : '—' },
  ]},
  { label: '', color: '#f97316', bodyColor: 'transparent', cols: [
      { key: 'dep_tratados',   label: 'Trat.',   fmt: (v: any) => v, headerSx: { color: C.white } },
  ]},
  { label: 'PRODUCAO', color: C.cyan, bodyColor: 'transparent', cols: [
      { key: 'hdt',   label: 'HD.T',  fmt: (v: any) => v },
      { key: 'media', label: 'Media', fmt: (v: any) => v },
  ]},
];

const ALL_COLS = COL_GROUPS.flatMap(g => g.cols);

const TC = ({ children, align = 'center', sx = {}, ...rest }: any) => (
  <TableCell align={align} {...rest} sx={{
    px: 0.8,
    py: 0.9,
    fontSize: '0.82rem',
    whiteSpace: 'nowrap',
    fontFamily: "'Inter', sans-serif",
    fontVariantNumeric: 'tabular-nums',
    color: C.white,
    borderBottom: `2px solid ${C.borderMid}`,
    borderRight: `2px solid ${C.borderMid}`,
    ...sx,
  }}>
    {children}
  </TableCell>
);

export default function ResumoSemanal({ onVoltar }: { onVoltar: () => void }) {
  const { token } = useAuth();
  const headers = { Authorization: 'Bearer ' + token };
  const [ciclos, setCiclos]     = useState<Ciclo[]>([]);
  const [areas, setAreas]       = useState<Area[]>([]);
  const [agentes, setAgentes]   = useState<Agente[]>([]);
  const [cicloId, setCicloId]   = useState('');
  const [areaId, setAreaId]     = useState('');
  const [agenteId, setAgenteId] = useState('');
  const [seFiltro, setSeFiltro] = useState('');
  const [dados, setDados]       = useState<ApiData | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro]         = useState('');
  const [celulaAtiva, setCelulaAtiva] = useState<{ linha: number; coluna: number } | null>(null);
  useEffect(() => {
    axios.get(API_URL + '/api/ciclos/', { headers }).then(r => {
      setCiclos(r.data);
      const ativo = r.data.find((c: Ciclo) => c.ativo);
      if (ativo) setCicloId(String(ativo.id));
    });
    axios.get(API_URL + '/api/areas/', { headers }).then(r => setAreas(r.data));
    axios.get(API_URL + '/api/agentes/', { headers }).then(r => setAgentes(r.data));
  }, []);

  const buscar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const params: any = {};
      if (cicloId)  params.ciclo_id  = cicloId;
      if (areaId)   params.area_id   = areaId;
      if (agenteId) params.agente_id = agenteId;
      if (seFiltro) params.se        = seFiltro;
      const r = await axios.get(API_URL + '/api/resumos/semanal', { headers, params });
      setDados(r.data);
    } catch { setErro('Erro ao carregar dados.'); }
    finally { setCarregando(false); }
  }, [cicloId, areaId, agenteId, seFiltro]);

  useEffect(() => { buscar(); }, [buscar]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!celulaAtiva) return;
      const totalLinhas  = dados?.linhas?.length ?? 0;
      const totalColunas = ALL_COLS.length;
      let { linha, coluna } = celulaAtiva;
      if (e.key === 'ArrowUp')         { linha  = Math.max(0, linha - 1);                e.preventDefault(); }
      else if (e.key === 'ArrowDown')  { linha  = Math.min(totalLinhas - 1, linha + 1);  e.preventDefault(); }
      else if (e.key === 'ArrowLeft')  { coluna = Math.max(0, coluna - 1);               e.preventDefault(); }
      else if (e.key === 'ArrowRight') { coluna = Math.min(totalColunas - 1, coluna + 1); e.preventDefault(); }
      else if (e.key === 'Tab') {
        if (e.shiftKey) coluna = Math.max(0, coluna - 1);
        else            coluna = Math.min(totalColunas - 1, coluna + 1);
        e.preventDefault();
      }
      else if (e.key === 'Enter')  { linha = Math.min(totalLinhas - 1, linha + 1); e.preventDefault(); }
      else if (e.key === 'Escape') { setCelulaAtiva(null); return; }
      else return;
      setCelulaAtiva({ linha, coluna });
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [celulaAtiva, dados]);

  const t = dados?.totais;

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: C.bg, display: 'flex', flexDirection: 'column' }}>
      <ModuleHeader
        titulo="Resumo Semanal"
        subtitulo="Por Dia · Visualização cronológica diária"
        icone={<BarChartIcon sx={{ fontSize: 32 }} />}
        accentColor="#1565C0"
        onVoltar={onVoltar}
        chips={dados ? [
          { label: 'dias', value: dados.linhas.length },
          { label: 'HD.T', value: dados.totais.hdt },
          { label: 'Med',  value: dados.totais.media + '/ag' },
        ] : undefined}
      />
      <Box sx={{ p: 2, maxWidth: '100%' }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField select label="Ciclo" value={cicloId} onChange={e => setCicloId(e.target.value)} size="small" sx={{ ...FIELD_SX, minWidth: 180 }}>
            <MenuItem value="">Todos os ciclos</MenuItem>
            {ciclos.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.nome}</MenuItem>)}
          </TextField>
          <TextField select label="Área" value={areaId} onChange={e => setAreaId(e.target.value)} size="small" sx={{ ...FIELD_SX, minWidth: 180 }}>
            <MenuItem value="">Todas as áreas</MenuItem>
            {areas.map(a => <MenuItem key={a.id} value={String(a.id)}>{a.nome}</MenuItem>)}
          </TextField>
          <TextField select label="Agente" value={agenteId} onChange={e => setAgenteId(e.target.value)} size="small" sx={{ ...FIELD_SX, minWidth: 200 }}>
            <MenuItem value="">Todos os agentes</MenuItem>
            {agentes.map(a => <MenuItem key={a.id} value={String(a.id)}>{a.nome} ({a.matricula})</MenuItem>)}
          </TextField>
          <TextField select label="SE" value={seFiltro} onChange={e => setSeFiltro(e.target.value)} size="small" sx={{ ...FIELD_SX, minWidth: 220 }}>
            <MenuItem value="">Todas as SEs</MenuItem>
            {(dados?.ses_disponiveis ?? []).map(s => (
              <MenuItem key={s} value={String(s)}>SE {s}</MenuItem>
            ))}
          </TextField>
        </Box>
        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
        {carregando && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
        {!carregando && dados?.linhas.length === 0 && <Alert severity="info">Nenhum registro encontrado.</Alert>}
        {dados && dados.linhas.length > 0 && !carregando && (
          <TableContainer component={Paper} elevation={0} sx={{
            borderRadius: 3.5,
            border: `1px solid ${C.border}`,
            overflow: 'auto',
            maxHeight: 'calc(100vh - 200px)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.03)',
            background: `linear-gradient(180deg, ${C.bgPanel} 0%, #050e1c 100%)`,
          }}>
            <Table size="small" stickyHeader>
              <TableHead>
                {/* Linha 1 — labels dos grupos */}
                <TableRow>
                  {COL_GROUPS.map(g => (
                    <TableCell key={g.label} colSpan={g.cols.length} align="center"
                      sx={{
                        backgroundColor: '#0a1830',
                        backgroundImage: `linear-gradient(180deg, ${g.color}40 0%, ${g.color}1c 100%)`,
                        color: C.white,
                        fontWeight: 800,
                        fontSize: 10.5,
                        fontFamily: "'Inter', sans-serif",
                        textTransform: 'uppercase',
                        letterSpacing: '0.12em',
                        borderBottom: `2px solid ${g.color}`,
                        borderRight: '2px solid rgba(255,255,255,0.25)',
                        py: 0.7,
                        px: 1.5,
                        textShadow: `0 0 8px ${g.color}55`,
                        position: 'sticky',
                        top: 0,
                        zIndex: 3,
                      }}>
                      {g.label}
                    </TableCell>
                  ))}
                </TableRow>
                {/* Linha 2 — subheaders das colunas */}
                <TableRow>
                  {COL_GROUPS.map(g => g.cols.map(c => (
                    <TableCell key={c.key} align="center"
                      sx={{
                        bgcolor: '#0a1830',
                        color: g.color,
                        fontWeight: 700,
                        fontSize: 10.5,
                        fontFamily: "'Inter', sans-serif",
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        px: 0.8,
                        py: 1.2,
                        borderBottom: `1px solid ${C.cyanBorder}`,
                        borderRight: `2px solid ${C.borderMid}`,
                        whiteSpace: 'nowrap',
                        position: 'sticky',
                        top: 28,
                        zIndex: 2,
                        ...(c.headerSx || {}),
                      }}>
                      {c.label}
                    </TableCell>
                  )))}
                </TableRow>
              </TableHead>
              <TableBody>
                {dados.linhas.map((l, idxLinha) => (
                  <TableRow key={l.data} sx={{
                    background: idxLinha % 2 === 0 ? C.bgRow : C.bgRowAlt,
                    '&:hover': { background: 'rgba(0,184,204,0.06)' },
                    transition: 'background 150ms ease',
                  }}>
                    {COL_GROUPS.map(g => g.cols.map(c => {
                      const idxColuna   = ALL_COLS.findIndex(ac => ac.key === c.key);
                      const linhaAtiva  = celulaAtiva?.linha === idxLinha;
                      const colunaAtiva = celulaAtiva?.coluna === idxColuna;
                      const ehAtiva     = linhaAtiva && colunaAtiva;
                      const naRegua     = (linhaAtiva || colunaAtiva) && !ehAtiva;
                      return (
                        <TC key={c.key} align="center"
                          onClick={() => {
                            console.log('CLIQUE:', { linha: idxLinha, coluna: idxColuna });
                            setCelulaAtiva({ linha: idxLinha, coluna: idxColuna });
                          }}
                          sx={{
                            cursor: 'pointer',
                            ...(c.key === 'data' ? { fontWeight: 700, color: '#f59e0b', fontSize: 17, textAlign: 'left', borderRight: `2px solid ${C.cyanBorder}` } : {}),
                            ...(c.key === 'quarteirao_concluido' ? { fontWeight: 600, color: C.cyan, borderRight: `2px solid ${C.cyanBorder}` } : {}),
                            ...(naRegua ? { backgroundColor: 'rgba(0,184,204,0.08) !important', color: `${C.cyan} !important` } : {}),
                            ...(ehAtiva ? { backgroundColor: `${C.orange} !important`, color: '#fff !important', fontWeight: 700 } : {}),
                          }}
                        >
                          {c.fmt(l[c.key])}
                        </TC>
                      );
                    }))}
                  </TableRow>
                ))}
                {t && (
                  <TableRow sx={{
                    background: '#0d2438',
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 2,
                    borderTop: `2px solid ${C.cyanBorder}`,
                    '& td': {
                      backgroundColor: '#0d2438',
                      color: C.cyan,
                      fontWeight: 800,
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '0.85rem',
                      letterSpacing: '0.04em',
                    },
                  }}>
                    <TC align="left">TOTAL</TC>
                    {(ALL_COLS.slice(1) as ColDef[]).map(c => (
                      <TC key={c.key}>{c.fmt((t as any)[c.key])}</TC>
                    ))}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}
